import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MdAttachMoney, MdCheckCircleOutline, MdChevronLeft, MdChevronRight, MdContentCopy, MdExtension, MdGroupAdd, MdPeople, MdStorefront, MdWarningAmber } from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import {
    fetchPartnerDashboard,
    fetchPartnerInstallations,
    fetchPartnerRetailers,
    fetchPartnerCommissions,
    fetchPartnerUsers,
    fetchPartnerProfile,
    updatePartnerProfile,
    createPartnerUser,
    removePartnerUser,
} from '../../../services/Http/PartnerApi';

const PAGE_SIZE = 25;
const SECTION_PATHS = ['/installations', '/retailers', '/commissions', '/users', '/settings', '/profile'];

const PartnerDashboard = () => {
    const { t, i18n } = useTranslation();
    const { pathname } = useLocation();
    const path = pathname.replace(/\/$/, '') || '/';
    const [data, setData] = useState(null);
    const [profile, setProfile] = useState(null);
    const [canManageTeam, setCanManageTeam] = useState(false);
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [actionError, setActionError] = useState(null);

    const loadRows = useCallback(async (section, requestedPage) => {
        const params = { page: requestedPage, per_page: PAGE_SIZE };
        const fetcher = section === '/installations' ? fetchPartnerInstallations
            : section === '/retailers' ? fetchPartnerRetailers
                : section === '/commissions' ? fetchPartnerCommissions : fetchPartnerUsers;
        const response = await fetcher(params);
        setRows(response.data || []);
        setPagination({ current_page: response.current_page || requestedPage, last_page: response.last_page || 1, total: response.total || 0 });
    }, []);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        setActionError(null);
        try {
            if (path === '/') {
                setData(await fetchPartnerDashboard());
            } else if (path === '/settings' || path === '/profile') {
                const response = await fetchPartnerProfile();
                setProfile(response.data);
            } else if (path === '/users') {
                const [response] = await Promise.all([
                    fetchPartnerProfile(),
                    loadRows(path, page),
                ]);
                setCanManageTeam(Boolean(response.data?.permissions?.manage_team));
            } else if (SECTION_PATHS.includes(path)) {
                await loadRows(path, page);
            }
        } catch (requestError) {
            setError(requestError);
        } finally {
            setLoading(false);
        }
    }, [loadRows, page, path]);

    useEffect(() => { reload(); }, [reload]);

    const copyReferral = async () => {
        try {
            await navigator.clipboard.writeText(data.profile.referral_code);
            setNotice(t('PartnerDashboard.copied'));
            setActionError(null);
        } catch {
            setActionError(t('PartnerDashboard.copyError'));
        }
    };

    const saveSettings = async (event) => {
        event.preventDefault();
        setSaving(true);
        setActionError(null);
        setNotice(null);
        const form = event.currentTarget;
        try {
            const response = await updatePartnerProfile({ payout_email: form.elements.payout_email.value || null });
            setProfile(response.data);
            setNotice(t('PartnerDashboard.settingsSaved'));
        } catch {
            setActionError(t('PartnerDashboard.saveError'));
        } finally {
            setSaving(false);
        }
    };

    const addUser = async (event) => {
        event.preventDefault();
        setSaving(true);
        setActionError(null);
        setNotice(null);
        const form = event.currentTarget;
        try {
            await createPartnerUser({
                name: form.elements.name.value.trim(),
                surname: form.elements.surname.value.trim(),
                email: form.elements.email.value.trim(),
                password: form.elements.password.value,
            });
            form.reset();
            setNotice(t('PartnerDashboard.memberAdded'));
            await loadRows('/users', page);
        } catch (requestError) {
            setActionError(requestError?.response?.data?.message || t('PartnerDashboard.memberAddError'));
        } finally {
            setSaving(false);
        }
    };

    const deleteUser = async (userId) => {
        if (!window.confirm(t('PartnerDashboard.removeConfirm'))) return;
        setActionError(null);
        setNotice(null);
        try {
            await removePartnerUser(userId);
            if (rows.length === 1 && page > 1) {
                setPage(page - 1);
                setLoading(true);
            } else {
                await loadRows('/users', page);
            }
            setPagination((current) => ({ ...current, total: Math.max(0, current.total - 1) }));
            setNotice(t('PartnerDashboard.memberRemoved'));
        } catch (requestError) {
            setActionError(t('PartnerDashboard.memberRemoveError'));
        }
    };

    const changePage = (nextPage) => {
        setPage(Math.max(1, Math.min(pagination.last_page, nextPage)));
        setLoading(true);
    };

    const currencyFormatter = useMemo(() => new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), [i18n.language]);
    const money = (amount, currency = 'EUR') => {
        try {
            return new Intl.NumberFormat(i18n.language, { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(amount) || 0);
        } catch {
            return `${currencyFormatter.format(Number(amount) || 0)} ${currency}`;
        }
    };
    const date = (value) => value ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(value)) : '—';
    const label = (value) => {
        if (!value) return '—';
        const statusKey = `PartnerDashboard.statuses.${String(value).toLowerCase()}`;
        return i18n.exists(statusKey) ? t(statusKey) : String(value).replaceAll('_', ' ');
    };

    if (loading) return <PageContainer><LoadingMessage>{t('PartnerDashboard.loading')}</LoadingMessage></PageContainer>;
    if (error) return <PageContainer><ErrorMessage>{t('PartnerDashboard.loadError')}</ErrorMessage><ActionButton type="button" onClick={reload}>{t('PartnerDashboard.retry')}</ActionButton></PageContainer>;

    const pageTitle = path === '/retailers' ? t('PartnerDashboard.retailersTitle')
        : path === '/installations' ? t('PartnerDashboard.installationsTitle')
            : path === '/commissions' ? t('PartnerDashboard.commissionsTitle')
                : path === '/users' ? t('PartnerDashboard.teamTitle')
                    : path === '/settings' || path === '/profile' ? t('PartnerDashboard.settingsTitle')
                        : data?.organization?.name || t('PartnerDashboard.dashboardTitle');

    return (
        <PageContainer>
            <PageHeader><PageTitle>{pageTitle}</PageTitle></PageHeader>
            <PageContent>
                {notice && <Notice role="status"><MdCheckCircleOutline />{notice}</Notice>}
                {actionError && <InlineError role="alert"><MdWarningAmber />{actionError}</InlineError>}
                {path === '/' && data && (
                    <>
                        <Hero>
                            <HeroCopy>
                                <Eyebrow>{t('PartnerDashboard.partnerProgram')}</Eyebrow>
                                <HeroTitle>{t('PartnerDashboard.welcome', { name: data.organization.name })}</HeroTitle>
                                <HeroText>{t('PartnerDashboard.overviewIntro')}</HeroText>
                            </HeroCopy>
                            <ReferralBox>
                                <ReferralLabel>{t('PartnerDashboard.referralCode')}</ReferralLabel>
                                <ReferralCode>{data.profile.referral_code}</ReferralCode>
                                <ActionButton type="button" onClick={copyReferral}><MdContentCopy />{t('PartnerDashboard.copyCode')}</ActionButton>
                            </ReferralBox>
                        </Hero>
                        <StatsGrid>
                            <Metric><MetricLabel>{t('PartnerDashboard.referredRetailers')}</MetricLabel><MetricValue>{data.metrics.referred_retailers ?? 0}</MetricValue><MetricHint>{t('PartnerDashboard.referredRetailersHint')}</MetricHint><MetricIcon><MdStorefront /></MetricIcon></Metric>
                            <Metric><MetricLabel>{t('PartnerDashboard.activeSubscriptions')}</MetricLabel><MetricValue>{data.metrics.active_retailers ?? 0}</MetricValue><MetricHint>{t('PartnerDashboard.activeSubscriptionsHint')}</MetricHint><MetricIcon><MdPeople /></MetricIcon></Metric>
                            <Metric><MetricLabel>{t('PartnerDashboard.installations')}</MetricLabel><MetricValue>{data.metrics.installations ?? 0}</MetricValue><MetricHint>{t('PartnerDashboard.installationsHint')}</MetricHint><MetricIcon><MdExtension /></MetricIcon></Metric>
                            <MoneyMetric title={t('PartnerDashboard.pendingCommission')} values={data.metrics.pending_commission} format={money} icon={<MdAttachMoney />} empty={t('PartnerDashboard.noCommission')} />
                            <MoneyMetric title={t('PartnerDashboard.paidCommission')} values={data.metrics.paid_commission} format={money} icon={<MdCheckCircleOutline />} empty={t('PartnerDashboard.noCommission')} />
                            <MoneyMetric title={t('PartnerDashboard.reversalDue')} values={data.metrics.reversal_due} format={money} icon={<MdWarningAmber />} empty={t('PartnerDashboard.noReversal')} warning />
                        </StatsGrid>
                        <SummaryNote>
                            <strong>{t('PartnerDashboard.commissionRate', { rate: data.profile.commission_rate })}</strong>
                            <span>{t('PartnerDashboard.commissionExplanation')}</span>
                        </SummaryNote>
                        <SetupPrompt>
                            <div><PanelTitle>{t('PartnerDashboard.payoutSetupTitle')}</PanelTitle><PanelText>{t('PartnerDashboard.payoutSetupText')}</PanelText></div>
                            <ActionLink to="/settings">{t('PartnerDashboard.managePayout')}</ActionLink>
                        </SetupPrompt>
                    </>
                )}

                {(path === '/settings' || path === '/profile') && profile && (
                    <SettingsPanel>
                        <PanelTitle>{t('PartnerDashboard.payoutTitle')}</PanelTitle>
                        <PanelText>{t('PartnerDashboard.payoutDescription')}</PanelText>
                        <SettingsSummary>
                            <SummaryItem><SummaryLabel>{t('PartnerDashboard.referralCode')}</SummaryLabel><strong>{profile.referral_code}</strong></SummaryItem>
                            <SummaryItem><SummaryLabel>{t('PartnerDashboard.commissionRateLabel')}</SummaryLabel><strong>{profile.commission_rate}%</strong></SummaryItem>
                            <SummaryItem><SummaryLabel>{t('PartnerDashboard.accountStatus')}</SummaryLabel><StatusPill $active={profile.status === 'active'}>{label(profile.status)}</StatusPill></SummaryItem>
                        </SettingsSummary>
                        {Object.prototype.hasOwnProperty.call(profile, 'payout_email') && (
                            <form onSubmit={saveSettings}>
                                <Label htmlFor="payout_email">{t('PartnerDashboard.payoutEmail')}</Label>
                                <Inline><Input id="payout_email" name="payout_email" type="email" defaultValue={profile.payout_email || ''} placeholder={t('PartnerDashboard.payoutEmailPlaceholder')} /><ActionButton type="submit" disabled={saving}>{saving ? t('PartnerDashboard.saving') : t('PartnerDashboard.save')}</ActionButton></Inline>
                                <HelpText>{t('PartnerDashboard.payoutEmailHelp')}</HelpText>
                            </form>
                        )}
                        {!Object.prototype.hasOwnProperty.call(profile, 'payout_email') && <Empty>{t('PartnerDashboard.adminOnlySettings')}</Empty>}
                    </SettingsPanel>
                )}

                {path === '/users' && <>
                    {canManageTeam ? <SettingsPanel>
                        <PanelTitle>{t('PartnerDashboard.addMemberTitle')}</PanelTitle>
                        <PanelText>{t('PartnerDashboard.addMemberText')}</PanelText>
                        <UserForm onSubmit={addUser}>
                            <Input name="name" autoComplete="given-name" placeholder={t('PartnerDashboard.firstName')} required />
                            <Input name="surname" autoComplete="family-name" placeholder={t('PartnerDashboard.lastName')} required />
                            <Input name="email" type="email" autoComplete="email" placeholder={t('PartnerDashboard.email')} required />
                            <Input name="password" type="password" autoComplete="new-password" placeholder={t('PartnerDashboard.temporaryPassword')} minLength="8" required />
                            <ActionButton type="submit" disabled={saving}><MdGroupAdd />{saving ? t('PartnerDashboard.adding') : t('PartnerDashboard.addMember')}</ActionButton>
                        </UserForm>
                        <HelpText>{t('PartnerDashboard.temporaryPasswordHelp')}</HelpText>
                    </SettingsPanel> : <Notice role="status">{t('PartnerDashboard.teamReadOnly')}</Notice>}
                    <DataTable rows={rows} section="users" t={t} date={date} label={label} onDelete={deleteUser} canManageTeam={canManageTeam} />
                    <Pager page={pagination.current_page} lastPage={pagination.last_page} total={pagination.total} onChange={changePage} t={t} />
                </>}

                {['/retailers', '/installations', '/commissions'].includes(path) && <>
                    <DataTable rows={rows} section={path.slice(1)} t={t} date={date} money={money} label={label} />
                    <Pager page={pagination.current_page} lastPage={pagination.last_page} total={pagination.total} onChange={changePage} t={t} />
                </>}
            </PageContent>
        </PageContainer>
    );
};

const MoneyMetric = ({ title, values = [], format, icon, empty, warning = false }) => (
    <Metric $warning={warning}>
        <MetricLabel>{title}</MetricLabel>
        <MoneyValues>{values.length ? values.map(({ currency, amount }) => <MetricValue key={currency}>{format(amount, currency)}</MetricValue>) : <MetricValue>{format(0, 'EUR')}</MetricValue>}</MoneyValues>
        <MetricHint>{values.length ? values.map((item) => item.currency).join(' · ') : empty}</MetricHint>
        <MetricIcon>{icon}</MetricIcon>
    </Metric>
);

const DataTable = ({ rows, section, t, date, money, label, onDelete, canManageTeam = false }) => {
    const columns = {
        users: [
            ['name', t('PartnerDashboard.name'), (row) => `${row.name} ${row.surname || ''}`.trim()],
            ['email', t('PartnerDashboard.email')],
            ['created_at', t('PartnerDashboard.added'), (row) => date(row.created_at)],
        ],
        retailers: [
            ['name', t('PartnerDashboard.retailer')],
            ['plan_name', t('PartnerDashboard.plan'), (row) => row.plan_name || row.plan_code || t('PartnerDashboard.noPaidPlan')],
            ['organization_active', t('PartnerDashboard.account'), (row) => <StatusPill $active={Boolean(row.organization_active)}>{row.organization_active ? t('PartnerDashboard.active') : t('PartnerDashboard.inactive')}</StatusPill>],
            ['subscription_status', t('PartnerDashboard.subscription'), (row) => <StatusPill $active={row.subscription_status === 'active' && row.is_subscribed}>{row.is_subscribed && row.subscription_status === 'active' ? t('PartnerDashboard.active') : label(row.subscription_status || 'not_subscribed')}</StatusPill>],
            ['current_period_end', t('PartnerDashboard.periodEnd'), (row) => date(row.current_period_end)],
        ],
        installations: [
            ['retailer_name', t('PartnerDashboard.retailer')],
            ['platform', t('PartnerDashboard.platform'), (row) => label(row.platform)],
            ['store_name', t('PartnerDashboard.store')],
            ['plugin_version', t('PartnerDashboard.version')],
            ['status', t('PartnerDashboard.status'), (row) => <StatusPill $active={['connected', 'active'].includes(row.status)}>{label(row.status)}</StatusPill>],
            ['last_seen_at', t('PartnerDashboard.lastSeen'), (row) => date(row.last_seen_at)],
        ],
        commissions: [
            ['retailer_name', t('PartnerDashboard.retailer')],
            ['plan_name', t('PartnerDashboard.plan'), (row) => row.plan_name || row.plan_code || '—'],
            ['gross_amount', t('PartnerDashboard.subscriptionPayment'), (row) => money(row.gross_amount, row.currency)],
            ['refunded_amount', t('PartnerDashboard.refunded'), (row) => money(row.refunded_amount, row.currency)],
            ['commission_rate', t('PartnerDashboard.rate'), (row) => `${row.commission_rate}%`],
            ['net_commission_amount', t('PartnerDashboard.netCommission'), (row) => money(row.net_commission_amount ?? row.commission_amount, row.currency)],
            ['reversal_due_amount', t('PartnerDashboard.reversalDueAmount'), (row) => money(row.reversal_due_amount, row.currency)],
            ['status', t('PartnerDashboard.status'), (row) => <StatusPill $active={row.status === 'paid'}>{label(row.status)}</StatusPill>],
            ['created_at', t('PartnerDashboard.date'), (row) => date(row.created_at)],
            ['paid_at', t('PartnerDashboard.paidAt'), (row) => date(row.paid_at)],
        ],
    }[section] || [];
    const heading = section === 'users' ? t('PartnerDashboard.teamTitle') : section === 'retailers' ? t('PartnerDashboard.retailersTitle') : section === 'installations' ? t('PartnerDashboard.installationsTitle') : t('PartnerDashboard.commissionsTitle');

    return <TableSection>
        <PanelTitle>{heading}</PanelTitle>
        <TableWrap>
            <Table><thead><tr>{columns.map(([key, title]) => <th key={key}>{title}</th>)}{section === 'users' && canManageTeam && <th>{t('PartnerDashboard.actions')}</th>}</tr></thead>
                <tbody>{rows.map((row) => <tr key={row.id}>{columns.map(([key, title, render]) => <td key={key}>{render ? render(row) : row[key] ?? '—'}</td>)}{section === 'users' && canManageTeam && <td><RemoveButton type="button" onClick={() => onDelete(row.id)}>{t('PartnerDashboard.remove')}</RemoveButton></td>}</tr>)}</tbody>
            </Table>
            {!rows.length && <Empty>{t('PartnerDashboard.empty')}</Empty>}
        </TableWrap>
    </TableSection>;
};

const Pager = ({ page, lastPage, total, onChange, t }) => lastPage > 1 && (
    <PagerBar><span>{t('PartnerDashboard.results', { total })}</span><PagerActions><IconButton type="button" disabled={page <= 1} onClick={() => onChange(page - 1)} aria-label={t('PartnerDashboard.previous')}><MdChevronLeft /></IconButton><span>{t('PartnerDashboard.page', { page, lastPage })}</span><IconButton type="button" disabled={page >= lastPage} onClick={() => onChange(page + 1)} aria-label={t('PartnerDashboard.next')}><MdChevronRight /></IconButton></PagerActions></PagerBar>
);

const Hero = styled.section`display:flex;justify-content:space-between;gap:2rem;align-items:center;background:#f5f7f8;border:1px solid #dce3e5;border-radius:6px;padding:1.75rem;margin-bottom:1.25rem;@media(max-width:720px){flex-direction:column;align-items:stretch}`;
const HeroCopy = styled.div`max-width:650px;min-width:0`;
const Eyebrow = styled.div`color:#47756c;font-size:.76rem;font-weight:700;text-transform:uppercase;margin-bottom:.45rem`;
const HeroTitle = styled.h2`margin:0 0 .45rem;font-size:1.55rem;color:#1f2933;line-height:1.25`;
const HeroText = styled.p`margin:0;color:#52616b;line-height:1.55`;
const ReferralBox = styled.div`background:#fff;border:1px solid #dce3e5;border-radius:6px;padding:1rem;min-width:235px;@media(max-width:720px){min-width:0}`;
const ReferralLabel = styled.div`font-size:.8rem;color:#62717a`;
const ReferralCode = styled.div`font-size:1.3rem;font-weight:700;word-break:break-all;color:#285d55;margin:.4rem 0 .8rem`;
const StatsGrid = styled.div`display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.85rem;margin-bottom:1.25rem;@media(max-width:900px){grid-template-columns:repeat(2,minmax(0,1fr))}@media(max-width:560px){grid-template-columns:1fr}`;
const Metric = styled.section`position:relative;min-width:0;background:#fff;border:1px solid ${({ $warning }) => $warning ? '#eadbb9' : '#dfe5e7'};border-radius:6px;padding:1rem 3rem 1rem 1rem;min-height:103px;box-sizing:border-box`;
const MetricLabel = styled.div`color:#53636d;font-size:.8rem;font-weight:650`;
const MetricValue = styled.div`color:#202b32;font-size:1.35rem;font-weight:700;line-height:1.35;overflow-wrap:anywhere`;
const MoneyValues = styled.div`margin-top:.3rem;display:flex;flex-direction:column;gap:.12rem`;
const MetricHint = styled.div`color:#78868e;font-size:.76rem;margin-top:.28rem`;
const MetricIcon = styled.div`position:absolute;right:1rem;top:1rem;color:#4f8178;font-size:1.35rem;display:flex`;
const SummaryNote = styled.section`display:flex;flex-direction:column;gap:.3rem;padding:.95rem 1rem;margin-bottom:1rem;border-left:3px solid #4f8178;background:#f6f8f8;color:#4d5b63;line-height:1.5;border-radius:0 4px 4px 0`;
const SetupPrompt = styled.section`display:flex;justify-content:space-between;align-items:center;gap:1rem;padding:1rem 0;border-top:1px solid #e2e7e8;border-bottom:1px solid #e2e7e8;@media(max-width:600px){align-items:flex-start;flex-direction:column}`;
const SettingsPanel = styled.section`background:#fff;border:1px solid #dfe5e7;border-radius:6px;padding:1.2rem;margin-bottom:1.2rem`;
const PanelTitle = styled.h2`margin:0 0 .35rem;color:#202b32;font-size:1.05rem`;
const PanelText = styled.p`margin:0 0 1rem;color:#596971;line-height:1.5`;
const SettingsSummary = styled.div`display:flex;gap:2.5rem;flex-wrap:wrap;padding:1rem 0;margin-bottom:1rem;border-top:1px solid #edf0f1;border-bottom:1px solid #edf0f1`;
const SummaryItem = styled.div`display:flex;flex-direction:column;gap:.3rem;color:#29363d`;
const SummaryLabel = styled.span`font-size:.76rem;color:#687780`;
const StatusPill = styled.span`display:inline-flex;width:max-content;max-width:100%;padding:.2rem .48rem;border-radius:4px;background:${({ $active }) => $active ? '#e7f3ed' : '#f1f2f2'};color:${({ $active }) => $active ? '#246848' : '#5d676d'};font-size:.78rem;text-transform:capitalize;white-space:normal`;
const Label = styled.label`display:block;font-weight:650;font-size:.88rem;margin-bottom:.4rem;color:#35434a`;
const Inline = styled.div`display:flex;gap:.5rem;max-width:560px;@media(max-width:480px){flex-direction:column}`;
const Input = styled.input`flex:1;min-width:170px;border:1px solid #cbd4d7;border-radius:4px;padding:.65rem .7rem;box-sizing:border-box`;
const ActionButton = styled.button`display:inline-flex;gap:.4rem;align-items:center;justify-content:center;border:0;border-radius:4px;background:#28685d;color:#fff;padding:.65rem .85rem;cursor:pointer;font:inherit;font-weight:600;white-space:nowrap;&:disabled{opacity:.55;cursor:wait}`;
const ActionLink = styled(Link)`display:inline-flex;align-items:center;justify-content:center;background:#28685d;color:#fff;text-decoration:none;border-radius:4px;padding:.65rem .85rem;font-weight:600;white-space:nowrap`;
const HelpText = styled.p`color:#75828a;font-size:.8rem;margin:.5rem 0 0;line-height:1.45`;
const UserForm = styled.form`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.65rem;align-items:center;@media(max-width:650px){grid-template-columns:1fr}`;
const TableSection = styled.section`margin-top:1.1rem`;
const TableWrap = styled.div`overflow-x:auto;background:#fff;border:1px solid #dfe5e7;border-radius:5px;margin-top:.6rem`;
const Table = styled.table`width:100%;border-collapse:collapse;font-size:.88rem;th,td{text-align:left;padding:.7rem .8rem;border-bottom:1px solid #edf0f1;vertical-align:middle}th{background:#f6f8f8;color:#52616a;font-size:.77rem;font-weight:650}td{color:#334149}tbody tr:last-child td{border-bottom:0}`;
const Empty = styled.div`padding:1.4rem;color:#718087;text-align:center`;
const RemoveButton = styled.button`border:1px solid #d6a3a3;background:#fff;color:#963838;border-radius:4px;padding:.35rem .55rem;cursor:pointer`;
const PagerBar = styled.div`display:flex;align-items:center;justify-content:space-between;gap:.75rem;padding:.8rem 0;color:#69777f;font-size:.82rem`;
const PagerActions = styled.div`display:flex;align-items:center;gap:.7rem`;
const IconButton = styled.button`width:32px;height:32px;border:1px solid #d6dfe1;background:#fff;border-radius:4px;font-size:1.25rem;line-height:1;cursor:pointer;&:disabled{opacity:.45;cursor:not-allowed}`;
const Notice = styled.div`display:flex;align-items:center;gap:.5rem;padding:.65rem .8rem;margin-bottom:.8rem;border:1px solid #c7e2d2;border-radius:4px;background:#eff8f2;color:#246848`;
const InlineError = styled.div`display:flex;align-items:center;gap:.5rem;padding:.65rem .8rem;margin-bottom:.8rem;border:1px solid #e5c5c5;border-radius:4px;background:#fff5f5;color:#923e3e`;

export default PartnerDashboard;
